<template>
  <div class="executive-summary-kpi-column" data-testid="executive-summary-kpi-column">
    <div class="executive-summary-kpi-column__head">
      <div class="big-number text-primary" data-testid="executive-summary-kpi-column-value">
        {{ formatReportNumber(value) }}
      </div>
      <div class="executive-summary-kpi-column__chart">
        <report-trend-chart
          v-if="points"
          :points="points"
          :delta="delta"
          :show-axes="false"
          :show-delta="false"
          filled
        />
      </div>
      <div class="subtitle2">{{ label }}</div>
      <div
        v-if="caption"
        class="body3 text-green-dark text-right"
        data-testid="executive-summary-kpi-column-caption"
      >
        {{ caption }}
      </div>
      <div
        v-else-if="trendCaption"
        class="body3 text-indigo-medium text-right"
        data-testid="executive-summary-kpi-column-trend"
        v-html="trendCaption"
      ></div>
    </div>

    <div v-for="row in rows" :key="row.id">
      <div class="d-flex justify-space-between text-primary py-2">
        <div class="body2">{{ row.label }}</div>
        <div class="body2">
          {{ formatReportNumber(row.value) }}
          <span
            v-if="row.valueNote"
            class="caption text-red-dark"
            data-testid="executive-summary-kpi-row-note"
          >
            {{ row.valueNote }}
          </span>
        </div>
      </div>
      <v-divider class="border-opacity-100" color="indigo-faint" />
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import ReportTrendChart from "@/components/reports/ReportTrendChart.vue";
import { formatReportNumber, formatReportTrendCaption } from "@/_helpers/reports";
import type { TrendPoint } from "@/_store/reports.module";

export interface ExecutiveSummaryKpiRow {
  /** Stable key: two OsType members both translate to "macOS", so `label` is not unique. */
  id: string;
  label: string;
  value: number;
  valueNote?: string;
}

export default defineComponent({
  components: { ReportTrendChart },
  props: {
    value: {
      type: Number,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    rows: {
      type: Array as PropType<ExecutiveSummaryKpiRow[]>,
      default: () => [],
    },
    points: {
      type: Array as PropType<TrendPoint[]>,
      default: undefined,
    },
    delta: {
      type: Number,
      default: undefined,
    },
    caption: {
      type: String,
      default: undefined,
    },
  },
  setup(props) {
    const trendCaption = computed(() => formatReportTrendCaption(props.delta));

    return { trendCaption, formatReportNumber };
  },
});
</script>

<style scoped lang="scss">
.executive-summary-kpi-column {
  min-width: 0;

  // A 2x2 grid, which is how the design lays this out: the sparkline shares the big number's band
  // and the trend caption shares the label's baseline. A flex row of two columns cannot do that —
  // the caption would sit below the chart, and so below the label.
  // `auto` for the headline so it never shrinks and the label never wraps mid-phrase; the chart
  // track takes the remainder and `minmax(0, …)` lets Chart.js shrink the canvas with it.
  &__head {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    align-items: end;
    column-gap: 24px;
    row-gap: 8px;
    margin-bottom: 8px;
  }

  // Reserves the sparkline's band even when there is no sparkline to draw. The tickets column
  // has no chart by design, and without this its grid row collapsed to the height of the number
  // alone — so `align-items: end` sat that number 21px higher than the other two, and the three
  // headline figures did not share a baseline. Kept in step with the band's own height, set on
  // `.report-trend-chart__canvas` in ReportTrendChart.vue.
  &__chart {
    min-width: 0;
    min-height: 72px;
  }
}
</style>
